/** Master-P2 Block 2.4 — Goal intake (campaign_briefs) list + detail. */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";

function StatusBadge({ status }) {
  const cls = {
    draft: "bg-slate-500",
    clarifying: "bg-amber-500",
    ready_prep_only: "bg-blue-500",
    ready_for_execution: "bg-emerald-600",
  }[status] || "bg-slate-400";
  return <Badge className={cls} data-testid={`brief-status-${status}`}>{status}</Badge>;
}

function KindChip({ kind }) {
  const cls = {
    fact: "bg-emerald-100 text-emerald-800 border-emerald-300",
    assumption: "bg-blue-100 text-blue-800 border-blue-300",
    open_required: "bg-amber-100 text-amber-800 border-amber-300",
  }[kind] || "bg-slate-100 text-slate-800";
  return <span className={`text-xs px-2 py-0.5 border rounded ${cls}`}
    data-testid={`kind-${kind}`}>{kind.replace("_", " ")}</span>;
}

export function GoalsList() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/briefs");
      setItems(r.data.items || []);
    } catch { toast.error("Ladefehler"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="goals-page">
      <header className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold">Ziele</h1>
          <p className="text-sm text-muted-foreground">
            Kampagnen-Briefs — was möchtest du erreichen?
          </p>
        </div>
        <Link to="/app/goals/new">
          <Button data-testid="btn-new-goal">Neues Ziel</Button>
        </Link>
      </header>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-left border-b">
              <tr>
                <th className="p-3">Ziel</th>
                <th className="p-3">Status</th>
                <th className="p-3">Version</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-b hover:bg-muted/50"
                      data-testid={`brief-row-${b.id}`}>
                  <td className="p-3">{(b.goal_free_text || "").slice(0, 80)}…</td>
                  <td className="p-3"><StatusBadge status={b.status} /></td>
                  <td className="p-3">v{b.version}</td>
                  <td className="p-3">
                    <Link to={`/app/goals/${b.id}`}
                            className="text-blue-600 hover:underline text-xs">Öffnen</Link>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">
                  Noch keine Ziele. Klicke „Neues Ziel".</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export function GoalNew() {
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  async function submit() {
    if (goal.trim().length < 5) {
      toast.error("Bitte formuliere dein Ziel etwas ausführlicher.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/briefs", { goal_free_text: goal });
      toast.success("Brief erstellt");
      nav(`/app/goals/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Fehler");
    } finally { setLoading(false); }
  }

  return (
    <div className="max-w-2xl space-y-4" data-testid="goal-new-page">
      <h1 className="text-2xl font-semibold">Was möchtest du erreichen?</h1>
      <p className="text-sm text-muted-foreground">
        Formuliere frei — die Plattform strukturiert dein Ziel und fragt bei
        offenen Punkten nach.
      </p>
      <Textarea rows={6} value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="Zum Beispiel: „20 Bewertungs-Leads in Wuppertal pro Monat, Budget 3000€"
                    data-testid="goal-textarea" />
      <Button onClick={submit} disabled={loading}
                data-testid="btn-submit-goal">
        {loading ? "Erstelle …" : "Ziel erfassen"}
      </Button>
    </div>
  );
}

export function GoalDetail() {
  const { id } = useParams();
  const [brief, setBrief] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/briefs/${id}`);
      setBrief(r.data);
    } catch { toast.error("Nicht gefunden"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function saveAnswer(field) {
    const raw = answers[field];
    if (!raw && raw !== 0) return;
    setBusy(true);
    try {
      let value = raw;
      if (field === "budget") {
        const n = parseInt(raw, 10);
        value = { amount_eur: isNaN(n) ? 0 : n, _user: true };
      } else if (typeof raw === "string") {
        value = { text: raw, _user: true };
      }
      await api.patch(`/briefs/${id}`,
        { structured_updates: { [field]: value } });
      setAnswers(a => ({ ...a, [field]: "" }));
      await load();
      toast.success("Gespeichert");
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    setBusy(false);
  }

  async function quickFillOpenFields() {
    /* Master-P2 correction — one-click clarification for UI users.
       Uses the bulk endpoint so the pattern is auditable and matches
       the API path testers use. */
    const open = (brief?.facts_vs_assumptions || [])
      .filter(c => c.kind === "open_required")
      .map(c => c.field);
    if (open.length === 0) return;
    setBusy(true);
    try {
      const defaults = {
        audience: "property_owners",
        offer: "immobilien_bewertung",
        region: "Wuppertal",
        success_event: "lead_valuation_request",
        budget: 3000,
      };
      const answers = {};
      for (const f of open) { if (defaults[f] !== undefined) answers[f] = defaults[f]; }
      await api.post(`/briefs/${id}/answer-clarification`, { answers });
      await load();
      toast.success("Standardantworten übernommen");
    } catch { toast.error("Fehler"); }
    setBusy(false);
  }

  async function approveBudget() {
    setBusy(true);
    try {
      await api.patch(`/briefs/${id}`, { budget_approved: true });
      await load();
      toast.success("Budget freigegeben");
    } catch { toast.error("Fehler"); }
    setBusy(false);
  }

  async function finalize() {
    setBusy(true);
    try {
      await api.post(`/briefs/${id}/finalize`);
      await load();
      toast.success("Finalisiert");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Fehler");
    }
    setBusy(false);
  }

  if (!brief) return <div className="p-4">Lade …</div>;
  const fva = brief.facts_vs_assumptions || [];
  const openReq = fva.filter(c => c.kind === "open_required");
  const factsAssumps = fva.filter(c => c.kind !== "open_required");

  return (
    <div className="space-y-4" data-testid="goal-detail-page">
      <header className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold">Ziel</h1>
          <p className="text-sm text-muted-foreground">
            v{brief.version} · <StatusBadge status={brief.status} />
          </p>
        </div>
        <div className="flex gap-2">
          {!brief.budget_approved && (
            <Button variant="outline" onClick={approveBudget} disabled={busy}
                       data-testid="btn-approve-budget">
              Budget freigeben
            </Button>
          )}
          <Button onClick={finalize} disabled={busy || openReq.length > 0}
                    data-testid="btn-finalize">
            Finalisieren
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader><CardTitle className="text-base">Freitext</CardTitle></CardHeader>
        <CardContent className="text-sm">{brief.goal_free_text}</CardContent>
      </Card>

      {openReq.length > 0 && (
        <Card data-testid="clarify-section">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-base text-amber-700">
                Offene Rückfragen ({openReq.length})
              </CardTitle>
              <Button size="sm" variant="outline" onClick={quickFillOpenFields}
                        disabled={busy} data-testid="btn-quick-fill">
                Mit Standard beantworten
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {openReq.map(c => (
              <div key={c.field} className="border-b pb-2"
                     data-testid={`clarify-${c.field}`}>
                <div className="font-medium text-sm">{c.reason || c.field}</div>
                <div className="text-xs text-muted-foreground mb-2">
                  Feld: <span className="font-mono">{c.field}</span>
                </div>
                <div className="flex gap-2">
                  <Input value={answers[c.field] || ""}
                            onChange={(e) => setAnswers(a =>
                              ({ ...a, [c.field]: e.target.value }))}
                            placeholder="Antwort …"
                            data-testid={`input-${c.field}`} />
                  <Button size="sm" onClick={() => saveAnswer(c.field)}
                            disabled={busy}
                            data-testid={`save-${c.field}`}>Speichern</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Struktur (Fakten & Annahmen)</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-left border-b">
              <tr><th className="p-2">Feld</th><th className="p-2">Kind</th>
                    <th className="p-2">Wert</th><th className="p-2">Begründung</th></tr>
            </thead>
            <tbody>
              {factsAssumps.map(c => (
                <tr key={c.field} className="border-b"
                      data-testid={`fva-${c.field}`}>
                  <td className="p-2 font-mono text-xs">{c.field}</td>
                  <td className="p-2"><KindChip kind={c.kind} /></td>
                  <td className="p-2 text-xs">
                    {c.value ? (typeof c.value === "object"
                                    ? JSON.stringify(c.value) : String(c.value))
                                : "—"}
                  </td>
                  <td className="p-2 text-xs text-muted-foreground">
                    {c.reason || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export default GoalsList;
